import React, { useState, useEffect } from 'react';
import { adminAPI, cacheUtils } from '../../services/api';
import { DataTable } from '../ui/data-table';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/modal';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Eye, EyeOff } from 'lucide-react';

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingAdminId, setEditingAdminId] = useState(null);
  const [deletingAdminId, setDeletingAdminId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    hashed_password: '',
    full_name: '',
    email: ''
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.listAdminUsers();
      console.log('Admins response:', response);
      setAdmins(response.data || []);
    } catch (error) {
      console.error('Error fetching admins:', error);
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      if (isEditing) {
        const updateData = {
          username: editingAdminId,
          full_name: formData.full_name,
          email: formData.email
        };
        
        // Only include password if it's provided
        if (formData.hashed_password) {
          updateData.hashed_password = formData.hashed_password;
        }
        
        console.log('Updating admin with data:', updateData);
        await adminAPI.updateAdminUser(updateData);
      } else {
        console.log('Creating admin with data:', formData);
        await adminAPI.createAdmin(formData);
      }

      // Clear admin cache to ensure fresh data
      cacheUtils.clearAdminCache();
      
      setIsModalOpen(false);
      resetForm();
      fetchAdmins();
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} admin:`, error);
      alert(`Failed to ${isEditing ? 'update' : 'create'} admin. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAdmin = async (admin) => {
    try {
      console.log('Editing admin:', admin);
      setIsEditing(true);
      setEditingAdminId(admin.username);
      
      // Fetch full admin details
      const response = await adminAPI.getAdminUserById(admin.username); 
      const adminDetails = response.data;
      
      setFormData({
        username: adminDetails.username || '',
        hashed_password: '', // Don't pre-fill password for security
        full_name: adminDetails.full_name || '',
        email: adminDetails.email || ''
      });
      
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching admin details:', error);
      alert('Failed to load admin details. Please try again.');
    }
  };

  const handleDeleteAdmin = async () => {
    if (!selectedAdmin) return;

    try {
      setDeletingAdminId(selectedAdmin.username);
      console.log('Deleting admin with username:', selectedAdmin.username);
      await adminAPI.deleteAdminUser(selectedAdmin.username);
      
      // Clear admin cache to ensure fresh data
      cacheUtils.clearAdminCache();
      
      setIsDeleteModalOpen(false);
      setSelectedAdmin(null);
      fetchAdmins();
    } catch (error) {
      console.error('Error deleting admin:', error);
      alert('Failed to delete admin. Please try again.');
    } finally {
      setDeletingAdminId(null);
    }
  };

  const openDeleteModal = (admin) => {
    setSelectedAdmin(admin);
    setIsDeleteModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      hashed_password: '',
      full_name: '',
      email: ''
    });
    setIsEditing(false);
    setEditingAdminId(null);
    setShowPassword(false);
  };

  const columns = [
    {
      accessorKey: 'username',
      header: 'Username',
      sortable: true
    },
    {
      accessorKey: 'full_name',
      header: 'Full Name',
      sortable: true
    },
    {
      accessorKey: 'email',
      header: 'Email',
      sortable: true
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      sortable: true,
      cell: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A'
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated At',
      sortable: true,
      cell: (row) => row.updated_at ? new Date(row.updated_at).toLocaleDateString() : 'N/A'
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      sortable: false,
      className: 'w-32',
      cell: (row) => (
        <div className="flex space-x-1">
          <button
            onClick={() => handleEditAdmin(row)}
            className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
            title="Edit admin"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => openDeleteModal(row)}
            disabled={deletingAdminId === row.username}
            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete admin"
          >
            {deletingAdminId === row.username ? (
              <div className="animate-spin w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="max-w-6xl mx-auto p-9">
      <div className="mb-8 border-b border-gray-200 pb-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Management</h1>
            <p className="text-lg text-gray-600">Manage system administrators and their accounts</p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            + Add Admin
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={admins}
        searchKey="full_name"
        searchPlaceholder="Search administrators..."
        loading={loading}
        emptyMessage="No administrators found. Create the first admin account!"
      />

      {/* Add/Edit Admin Modal */}
      <Modal isOpen={isModalOpen} onClose={() => {
        setIsModalOpen(false);
        resetForm();
      }}>
        <form onSubmit={handleSubmit}>
          <ModalHeader>
            <h2 className="text-xl font-semibold">
              {isEditing ? 'Edit Administrator' : 'Add New Administrator'}
            </h2>
          </ModalHeader>

          <ModalBody className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                Username *
              </label>
              <Input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleInputChange}
                required
                disabled={isEditing}
                placeholder="Enter username"
              />
              {isEditing && (
                <p className="text-xs text-gray-500 mt-1">Username cannot be changed</p>
              )}
            </div>

            <div>
              <label htmlFor="hashed_password" className="block text-sm font-medium text-gray-700 mb-1">
                Password {isEditing && '(leave blank to keep current password)'}
              </label>
              <div className="relative">
                <Input
                  id="hashed_password"
                  name="hashed_password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.hashed_password}
                  onChange={handleInputChange}
                  required={!isEditing}
                  placeholder={isEditing ? "Enter new password (optional)" : "Enter password"}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <Input
                id="full_name"
                name="full_name"
                type="text"
                value={formData.full_name}
                onChange={handleInputChange}
                required
                placeholder="Enter full name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Enter email address"
              />
            </div>
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
              className="mr-2 bg-gray-500 hover:bg-gray-600"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading 
                ? (isEditing ? 'Updating...' : 'Creating...') 
                : (isEditing ? 'Update Admin' : 'Create Admin')
              }
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal 
        isOpen={isDeleteModalOpen} 
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedAdmin(null);
        }}
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold">Delete Administrator</h2>
        </ModalHeader>
        
        <ModalBody>
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex-shrink-0">
              <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">Are you sure?</h3>
              <p className="text-gray-600">
                This action will permanently delete the administrator account for{' '}
                <span className="font-semibold">
                  {selectedAdmin?.full_name || selectedAdmin?.username}
                </span>. 
                This action cannot be undone.
              </p>
            </div>
          </div>
          
          {selectedAdmin && (
            <div className="bg-gray-50 rounded-md p-3">
              <div className="text-sm">
                <p><span className="font-medium">Username:</span> {selectedAdmin.username}</p>
                <p><span className="font-medium">Full Name:</span> {selectedAdmin.full_name}</p>
                <p><span className="font-medium">Email:</span> {selectedAdmin.email}</p>
              </div>
            </div>
          )}
        </ModalBody>
        
        <ModalFooter>
          <Button 
            onClick={() => {
              setIsDeleteModalOpen(false);
              setSelectedAdmin(null);
            }}
            disabled={deletingAdminId}
            className="mr-2 bg-gray-500 hover:bg-gray-600"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAdmin}
            disabled={deletingAdminId}
            className="bg-red-600 hover:bg-red-700"
          >
            {deletingAdminId ? 'Deleting...' : 'Delete Admin'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AdminManagement;